/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { useTheme } from '../theme';

const SellerPlanSelection = () => {
  const theme = useTheme();
  const [currentPlan, setCurrentPlan] = useState('Personal');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await authService.getProfile();
        const user = res.data.data;
        setCurrentPlan(user.sellerPlan || 'Personal');
        // Update local storage if needed
        localStorage.setItem('user', JSON.stringify(user));
      } catch (err) {
        console.error("Failed to fetch profile", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handlePlanSelect = async (planName: string) => {
    if (planName === currentPlan) return;
    
    setUpdating(planName);
    setError('');
    try {
      const res = await authService.updateProfile({ sellerPlan: planName });
      const updatedUser = res.data.data;
      setCurrentPlan(updatedUser.sellerPlan);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      // Optional: show success message or redirect
    } catch (err) {
      setError('Failed to update plan. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  const plans = [
    {
      name: 'Personal',
      price: '$0/mo',
      fee: 'Stripe Fee + 1.5% + $0.15',
      features: ['Basic listing', 'Public view', 'Standard support'],
      buttonText: currentPlan === 'Personal' ? 'Current Plan' : 'Select Personal',
      active: currentPlan === 'Personal'
    },
    {
      name: 'Pro',
      price: '$15/mo',
      fee: 'Stripe Fee + 0.75% + $0.10',
      features: [
        'Lower transaction fees', 
        'Bulk management', 
        'Advanced analytics', 
        'Create and manage ads',
        'Priority support'
      ],
      buttonText: currentPlan === 'Pro' ? 'Current Plan' : 'Upgrade to Pro',
      active: currentPlan === 'Pro',
      highlight: true
    }
  ];

  if (loading) {
    return <div className="flex justify-center p-12">Loading plans...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto py-12 px-4">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-black mb-4">Choose Your Seller Plan</h2>
        <p className="text-gray-600">Select the plan that best fits your business needs.</p>
        {error && <p className="mt-4 text-red-600 font-bold">{error}</p>}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {plans.map((plan) => (
          <div key={plan.name} className={`rounded-xl border p-8 shadow-sm transition-all flex flex-col ${plan.active ? 'border-green-500 ring-2 ring-green-500/20' : plan.highlight ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold">{plan.name} Seller</h3>
              {plan.active && <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded">ACTIVE</span>}
            </div>
            <p className="mt-4 text-4xl font-extrabold tracking-tight">{plan.price}</p>
            <p className="mt-1 text-sm text-indigo-600 font-medium">Fee: {plan.fee}</p>
            
            <ul className="mt-6 space-y-4 flex-grow">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start">
                  <svg className="h-5 w-5 text-indigo-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-3 text-gray-600 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button
              onClick={() => handlePlanSelect(plan.name)}
              disabled={plan.active || updating !== null}
              className={`mt-8 w-full py-3 px-6 rounded-lg font-bold transition-all ${
                plan.active 
                  ? 'bg-gray-100 text-gray-400 cursor-default' 
                  : updating === plan.name
                    ? 'bg-indigo-400 text-white cursor-wait'
                    : plan.highlight 
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-[1.02]' 
                      : 'bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 hover:scale-[1.02]'
              }`}
            >
              {updating === plan.name ? 'Updating...' : plan.buttonText}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SellerPlanSelection;
